import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, NumberInput, Text } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';

import { UI_COLORS } from 'constants/colors';

import showToast from 'actions/toastActions';
import { requestPutBulkDensityInput } from 'store/labSamples/requests';
import { BulkDensityResultsType, LabSampleType } from 'store/labSamples/types';
import ErrorAlert from 'common/ErrorAlert';

type BulkDensityInputFormProps = {
  sample?: LabSampleType | null;
  onSubmissionSuccess: VoidFunction;
};

const BulkDensityInputForm = ({ sample, onSubmissionSuccess }: BulkDensityInputFormProps) => {
  const { t } = useTranslation();
  const barcode = sample?.barcode || '';
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const totalDryMassRef = useRef<HTMLInputElement | null>(null);
  const coarseFractionMassRef = useRef<HTMLInputElement | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  const submitBDResultsMutation = useMutation({
    mutationFn: (bdResults: BulkDensityResultsType) =>
      requestPutBulkDensityInput(barcode, bdResults),
    onSuccess: () => {
      showToast(t('bulkDensityResultsSubmitted'), 'success');
      onSubmissionSuccess();
    },
  });

  if (sample && !sample.bulk_density_panel) {
    return <ErrorAlert content={t('bulkDensityNotOrdered')} />;
  }

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        return '';
      }
    },
    [hasUnsavedChanges],
  );

  useEffect(() => {
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      // Return a cleanup function that removes THIS specific handleBeforeUnload instance
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [hasUnsavedChanges, handleBeforeUnload]);

  useEffect(() => {
    totalDryMassRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    const hasErrors = Object.values(form.errors).some((error) => error !== undefined);
    if (hasErrors) {
      return;
    }

    const totalDryMass = parseFloat(form.getValues().totalDryMass);
    const coarseFractionMass = parseFloat(form.getValues().coarseFractionMass);
    const { fineFragmentMass } = form.getTransformedValues();

    const bdResults: BulkDensityResultsType = {
      total_dry_mass_105c_g: totalDryMass,
      total_coarse_dry_mass_105c_g: coarseFractionMass,
      total_fine_dry_mass_105c_g: fineFragmentMass,
    };
    submitBDResultsMutation.mutate(bdResults);

    setHasUnsavedChanges(false);
    form.reset();
  };

  const validateTotalDryMass = (value) => {
    if (!isNotEmpty(value)) {
      return t('totalDryMassRequired');
    }
    if (Number.isNaN(parseFloat(value))) {
      return t('totalDryMassNumber');
    }
    if (value > 1000) {
      return t('totalDryMassLess1000');
    }
    if (value < 0) {
      return t('totalDryMassGreater0');
    }
    return null;
  };

  const validateCoarseFractionMass = (value) => {
    if (!isNotEmpty(value)) {
      return t('coarseFractionMassRequired');
    }
    if (Number.isNaN(parseFloat(value))) {
      return t('coarseFractionMassNumber');
    }
    if (value > 1000) {
      return t('coarseFractionMassLess1000');
    }
    if (value < 0) {
      return t('coarseFractionMassGreater0');
    }
    return null;
  };

  const validateRelationship = (totalDryMass, coarseFractionMass) => {
    if (totalDryMass - coarseFractionMass < 0) {
      form.setFieldError('coarseFractionMass', t('coarseFracMassGreaterThanTotalDry'));
    } else if (coarseFractionMass > totalDryMass * 0.05) {
      form.setFieldError('coarseFractionMass', t('coarseFracMassMoreThan5Percent'));
    }
  };

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      totalDryMass: '',
      coarseFractionMass: '',
      fineFragmentMass: '',
    },
    transformValues: () => {
      const totalDryMass = parseFloat(form.getValues().totalDryMass);
      const coarseFractionMass = parseFloat(form.getValues().coarseFractionMass);

      if (Number.isNaN(totalDryMass) || Number.isNaN(coarseFractionMass)) {
        return {
          fineFragmentMass: 0,
        };
      }
      validateRelationship(totalDryMass, coarseFractionMass);
      return {
        fineFragmentMass: totalDryMass - coarseFractionMass,
      };
    },
    validate: {
      totalDryMass: (value) => validateTotalDryMass(value),
      coarseFractionMass: (value) => validateCoarseFractionMass(value),
    },
  });

  useEffect(() => {
    const formHasValues =
      form.getValues().totalDryMass !== '' || form.getValues().coarseFractionMass !== '';

    setHasUnsavedChanges(formHasValues);
  }, [form.getValues().totalDryMass, form.getValues().coarseFractionMass]);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement | HTMLButtonElement>,
  ) => {
    if (event.key === 'Enter' && ref.current) {
      event.preventDefault();
      ref.current.focus();
    }
  };

  return (
    <form onSubmit={form.onSubmit(() => handleSubmit())}>
      <NumberInput
        ref={totalDryMassRef}
        withAsterisk
        label={t('totalDryMass')}
        placeholder={t('totalDryMass')}
        key={form.key('totalDryMass')}
        {...form.getInputProps('totalDryMass')}
        onKeyDown={(event) => handleKeyDown(event, coarseFractionMassRef)}
      />
      <NumberInput
        ref={coarseFractionMassRef}
        withAsterisk
        label={t('coarseFractionMass')}
        placeholder={t('coarseFractionMass')}
        key={form.key('coarseFractionMass')}
        {...form.getInputProps('coarseFractionMass')}
        onKeyDown={(event) => handleKeyDown(event, submitButtonRef)}
      />

      <Text mt="md" size="sm" fw={500}>
        {t('fineFragmentMass')} : {form.getTransformedValues().fineFragmentMass}
      </Text>

      <Group justify="flex-end" mt="md">
        <Button color={UI_COLORS.error} type="reset">
          {t('cancel')}
        </Button>
        <Button ref={submitButtonRef} type="submit">
          {t('submit')}
        </Button>
      </Group>
    </form>
  );
};

export default BulkDensityInputForm;
